import { keepPreviousData } from "@tanstack/react-query";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import {
  ActivityShortlistsParams,
  useActivityShortlists as useActivityShortlistsQuery
} from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useActivitySuspendedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_suspended_shortlists";
import { Match, Meta, Shortlist } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { useMemo } from "react";

import { useShortlistsStore } from "../store/shortlists";
import { useCurrentActivity } from "./use_current_activity";

export type UseActivityShortlistsReturn = {
  shortlists: Shortlist[];
  suspendedShortlists: Shortlist[];
  allShortlists: Shortlist[];
  rankedShortlists: Match[];
  shortlistsMeta: Meta;
  suspendedShortlistsMeta: Meta;
  isLoadingShortlists: boolean;
};

export const useActivityShortlists = (): UseActivityShortlistsReturn => {
  const activity = useCurrentActivity();
  const { id } = activity;
  const { canSeeShortlist } = useActivityPermissions(activity);
  const shortlistsParams = useShortlistsStore((state) => state.shortlistsParams);

  const params: ActivityShortlistsParams = {
    ...shortlistsParams,
    highlights: true
  };

  const shortlistsQueryOptions = {
    enabled: canSeeShortlist,
    placeholderData: keepPreviousData
  };

  const { data: shortlistsData, isFetching: isLoadingShortlists } = useActivityShortlistsQuery(
    id,
    params,
    shortlistsQueryOptions
  );

  const { data: suspendedShortlistsData, isFetching: isLoadingSuspendedShortlists } =
    useActivitySuspendedShortlists(id, params, shortlistsQueryOptions);

  const isAtLeastOneShortlist =
    !!shortlistsData?.entries?.length || !!suspendedShortlistsData?.entries?.length;

  const { data: rankedShortlistsData } = useActivityRankedShortlists({
    activityId: id,
    options: {
      enabled: canSeeShortlist && isAtLeastOneShortlist
    }
  });

  const rankedShortlistsReady = rankedShortlistsData?.meta?.status === "ready";
  const shortlists = (shortlistsData?.entries as Shortlist[]) || [];
  const suspendedShortlists = (suspendedShortlistsData?.entries as Shortlist[]) || [];
  const allShortlists = [...shortlists, ...suspendedShortlists];

  const shortlistsMeta = useMemo(() => {
    if (!shortlistsData?.meta) {
      return { total: 0, page: 1 };
    }

    // activity id is sent in a request as a separate param, based on that api adds it to filters set.
    // having it defined in main filters is redundant
    // and cause issues when calculating filters state flags (are filters modified, etc.)
    if (shortlistsData.meta.filtersV2?.mainFilters[KnownFiltersV2.ActivityId]) {
      const { [KnownFiltersV2.ActivityId]: _, ...remainingMainFilters } =
        shortlistsData.meta.filtersV2.mainFilters;
      return {
        ...shortlistsData.meta,
        filtersV2: {
          ...shortlistsData.meta.filtersV2,
          mainFilters: remainingMainFilters
        }
      };
    }

    return shortlistsData.meta;
  }, [shortlistsData?.meta]);

  return {
    shortlists,
    shortlistsMeta,
    suspendedShortlists,
    suspendedShortlistsMeta: suspendedShortlistsData?.meta || { total: 0 },
    allShortlists,
    isLoadingShortlists: isLoadingShortlists || isLoadingSuspendedShortlists,
    rankedShortlists: rankedShortlistsReady ? rankedShortlistsData.entries : []
  };
};
